import { useEffect, useState } from "react";
import { ShieldAlert, Users, Music2, DollarSign, Landmark, Wallet2, Scissors } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getAdminOverview } from "@/lib/afmApi";
import { fmtNum, fmtUSD } from "@/lib/format";
import { Loading, SectionTitle } from "@/components/common";

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`admin-stat-${label}`}>
    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
      <Icon className={`h-4 w-4 ${accent || "text-slate-400"}`} /> {label}
    </div>
    <div className="mt-2 font-mono text-2xl font-bold tabular-nums text-black">{value}</div>
  </div>
);

export default function AdminOverview() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    getAdminOverview(user.id)
      .then((d) => { setData(d); setStatus("ok"); })
      .catch((e) => { setMsg(e?.response?.data?.detail || "Acceso restringido"); setStatus("denied"); });
  }, [user.id]);

  if (status === "loading") return <Loading />;
  if (status === "denied") {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="admin-denied">
        <div className="mb-4 rounded-full border border-amber-300 bg-amber-50 p-4 text-amber-700"><ShieldAlert className="h-7 w-7" /></div>
        <h3 className="font-serif text-xl font-bold text-black">Solo administradores</h3>
        <p className="mt-2 max-w-sm text-sm text-slate-500">{msg}</p>
      </div>
    );
  }

  const t = data.totals || {};
  const ranking = data.ranking || [];

  return (
    <div className="space-y-8" data-testid="admin-overview-page">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="font-serif text-3xl font-extrabold tracking-tight text-black">Panel · {data.label || "Always Fresh Music"}</h1>
          {!data.live_mode && <span className="rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">SIN DATOS REALES AÚN</span>}
        </div>
        <p className="mt-1 text-sm text-slate-500">Vista global de la distribuidora.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Users} label="Artistas" value={fmtNum(t.artists)} accent="text-[#7C5CFF]" />
        <Stat icon={Music2} label="Streams" value={fmtNum(t.streams)} accent="text-[#EC4899]" />
        <Stat icon={DollarSign} label="Bruto" value={fmtUSD(t.gross_usd)} accent="text-emerald-500" />
        <Stat icon={Scissors} label="Fee distribuidor (10%)" value={fmtUSD(t.distributor_fee_usd)} accent="text-slate-400" />
        <Stat icon={Landmark} label="Sello recibe (90%)" value={fmtUSD(t.label_received_usd)} accent="text-slate-500" />
        <Stat icon={Landmark} label="Comisión SONA" value={fmtUSD(t.sona_commission_usd)} accent="text-slate-500" />
        <Stat icon={Wallet2} label="Neto artistas" value={fmtUSD(t.artist_net_usd)} accent="text-[#AC3333]" />
        <Stat icon={DollarSign} label="Pagado" value={fmtUSD(t.paid_usd)} accent="text-blue-500" />
        <Stat icon={Wallet2} label="Disponible" value={fmtUSD(t.available_usd)} accent="text-emerald-600" />
      </div>

      <div>
        <SectionTitle>Ranking de artistas</SectionTitle>
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-semibold">Artista</th>
                  <th className="px-5 py-3 text-right font-semibold">Streams</th>
                  <th className="px-5 py-3 text-right font-semibold">Bruto</th>
                  <th className="px-5 py-3 text-right font-semibold">Neto</th>
                  <th className="px-5 py-3 text-right font-semibold">Pagado</th>
                  <th className="px-5 py-3 text-right font-semibold">Disponible</th>
                </tr>
              </thead>
              <tbody>
                {ranking.map((a) => (
                  <tr key={a.user_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-artist-row-${a.user_id}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium text-black">{a.name}</div>
                      <div className="text-xs text-slate-500">{a.stage_name || a.email}</div>
                    </td>
                    <td className="px-5 py-3 text-right font-mono tabular-nums text-slate-700">{fmtNum(a.streams)}</td>
                    <td className="px-5 py-3 text-right font-mono tabular-nums text-slate-700">{fmtUSD(a.gross_usd)}</td>
                    <td className="px-5 py-3 text-right font-mono tabular-nums font-semibold text-black">{fmtUSD(a.net_usd)}</td>
                    <td className="px-5 py-3 text-right font-mono tabular-nums text-slate-500">{fmtUSD(a.paid_usd)}</td>
                    <td className="px-5 py-3 text-right font-mono tabular-nums text-emerald-600">{fmtUSD(a.available_usd)}</td>
                  </tr>
                ))}
                {!ranking.length && <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-slate-400">Sin artistas.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
